import { useEffect, useRef, type CSSProperties } from 'react';
import posterAvif1280 from '@/assets/videos/process-poster-1280.avif';
import posterWebp1280 from '@/assets/videos/process-poster-1280.webp';
import posterAvif640 from '@/assets/videos/process-poster-640.avif';
import posterWebp640 from '@/assets/videos/process-poster-640.webp';
import { AppLink } from '@/components/ui/AppLink';
import { Button } from '@/components/ui/Button';
import { afterQuiet } from '@/lib/hydration-gate';
import { useT } from '@/lib/i18n';
import { filmMode, useQualityStore } from '@/lib/quality';
import { Magnetic } from '@/motion/Magnetic';
import './hero-compact.css';

/** The film is an enhancement over its own first frame: it starts once the page has been quiet for this long. */
const FILM_START_DELAY_MS = 1200;

/**
 * Home hero (D-331): a film of the stone's journey, full screen, with the headline, one line of help and the two main
 * buttons over it. Nothing else — the animated WebGL slab of the first design is gone.
 *
 *   poster <picture>   the film's opening frame, the LCP image; the only picture on `lite` / `minimal` tiers
 *   <video>            added after load on every device (`./hero/film`, its own chunk), fades in over the poster; it
 *                      waits for the Play button only with reduced motion, Save-Data or a slow connection (D-335)
 *   vignette + copy    eyebrow · headline · lead · buttons rise into view at once, 80 ms apart (pure CSS, no JS needed)
 *
 * The page is complete without the film (poster and words); a visible button stops and starts it (WCAG 2.2.2).
 */
export function Hero() {
  const t = useT();
  // the film starts once the device has been measured (before that the tier is a guess)
  const detected = useQualityStore((state) => state.detected);
  const stageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !detected) return undefined;
    const manual = filmMode() === 'manual';
    let disposed = false;
    let stopFilm: (() => void) | undefined;
    void afterQuiet(FILM_START_DELAY_MS)
      .then(() => (disposed ? null : import('./hero/film')))
      .then((film) => {
        if (!film || disposed) return;
        stopFilm = film.startHeroFilm(stage, { manual });
      });
    return () => {
      disposed = true;
      stopFilm?.();
    };
  }, [detected]);

  return (
    <section className="hero on-dark" aria-labelledby="hero-title">
      <div className="hero__stage">
        <div ref={stageRef} className="hero__media">
          <picture className="hero__poster">
            <source
              type="image/avif"
              srcSet={`${posterAvif640} 640w, ${posterAvif1280} 1280w`}
              sizes="100vw"
            />
            <source
              type="image/webp"
              srcSet={`${posterWebp640} 640w, ${posterWebp1280} 1280w`}
              sizes="100vw"
            />
            <img
              src={posterWebp1280}
              alt=""
              width={1280}
              height={720}
              fetchPriority="high"
              decoding="async"
            />
          </picture>
          <div className="hero__vignette" aria-hidden="true" />
        </div>

        <div className="hero__copy">
          <p className="hero__eyebrow t-eyebrow" style={{ '--d': '0s' } as CSSProperties}>
            {t('hero.eyebrow')}
          </p>
          <h1
            id="hero-title"
            className="hero__title t-display"
            style={{ '--d': '0.08s' } as CSSProperties}
          >
            {t('hero.title')}
          </h1>
          <p className="hero__lead t-lead" style={{ '--d': '0.16s' } as CSSProperties}>
            {t('hero.lead')}
          </p>
          <div className="hero__actions" style={{ '--d': '0.24s' } as CSSProperties}>
            <Magnetic>
              <Button asChild size="lg">
                <AppLink to="/shop">{t('hero.cta.shop')}</AppLink>
              </Button>
            </Magnetic>
            <Button asChild size="lg" variant="glass">
              <AppLink to="/visualizer">{t('hero.cta.visualizer')}</AppLink>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
